import { baseApi } from "@/shared/api";
import type { SigningActionResponse } from "@/shared/types";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type {
  ServiceContractApiPayload,
  ServiceContractResponse,
  ServiceContractContract,
  ServiceContractListResponse,
  ContractorSocialCategory,
  EnrollmentFileUploadResponse,
} from "../types";

export const serviceContractApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    submitServiceContract: builder.mutation<ServiceContractResponse, ServiceContractApiPayload>({
      query: (body) => ({
        url: "/api/legal/service-agreements-individual/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ServiceAgreementsIndividual" as const, id: "LIST" }],
    }),

    updateServiceContract: builder.mutation<ServiceContractResponse, { id: number; data: ServiceContractApiPayload }>({
      query: ({ id, data }) => ({
        url: `/api/legal/service-agreements-individual/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ServiceAgreementsIndividual" as const, id },
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
      ],
    }),

    getServiceContract: builder.query<ServiceContractResponse, number>({
      query: (id) => ({
        url: `/api/legal/service-agreements-individual/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "ServiceAgreementsIndividual" as const, id }],
    }),

    getServiceContractList: builder.query<ServiceContractContract[], void>({
      query: () => ({
        url: `/api/legal/service-agreements-individual/`,
        method: "GET",
      }),
      transformResponse: (response: ServiceContractListResponse): ServiceContractContract[] => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "ServiceAgreementsIndividual" as const, id })),
              { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
            ]
          : [{ type: "ServiceAgreementsIndividual" as const, id: "LIST" }],
    }),

    submitServiceContractForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
      query: ({ id, cms_signature, file_base64 }) => ({
        url: `/api/legal/service-agreements-individual/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature, file_base64 },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          serviceContractApi.util.updateQueryData("getServiceContract", id, (draft) => {
            draft.signing_status = 1;
          }),
        );
      },
    }),

    downloadServiceContractPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/legal/service-agreements-individual/${id}/preview/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    deleteServiceContract: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/service-agreements-individual/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ServiceAgreementsIndividual" as const, id },
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
      ],
    }),

    revokeServiceContract: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/service-agreements-individual/${id}/revoke/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ServiceAgreementsIndividual" as const, id },
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
        "PendingActions",
      ],
    }),

    uploadEnrollmentFile: builder.mutation<EnrollmentFileUploadResponse, { contractId: number; file: File }>({
      query: ({ contractId, file }) => {
        const formData = new FormData();
        formData.append("document_file", file);
        return {
          url: `/api/legal/service-agreements-individual/${contractId}/enrollment-file/`,
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: "ServiceAgreementsIndividual" as const, id: contractId },
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
      ],
    }),

    deleteEnrollmentFile: builder.mutation<void, number>({
      query: (contractId) => ({
        url: `/api/legal/service-agreements-individual/${contractId}/enrollment-file/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, contractId) => [
        { type: "ServiceAgreementsIndividual" as const, id: contractId },
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
      ],
    }),

    createContractorSocialCategory: builder.mutation<ContractorSocialCategory, { contractId: number; data: FormData }>({
      query: ({ contractId, data }) => ({
        url: `/api/legal/service-agreements-individual/${contractId}/social-categories/`,
        method: "POST",
        body: data,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: "ServiceAgreementsIndividual" as const, id: contractId },
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
      ],
    }),

    updateContractorSocialCategory: builder.mutation<
      ContractorSocialCategory,
      { contractId: number; categoryId: number; data: FormData }
    >({
      query: ({ contractId, categoryId, data }) => ({
        url: `/api/legal/service-agreements-individual/${contractId}/social-categories/${categoryId}/`,
        method: "PATCH",
        body: data,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: "ServiceAgreementsIndividual" as const, id: contractId },
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
      ],
    }),

    deleteContractorSocialCategory: builder.mutation<void, { contractId: number; categoryId: number }>({
      query: ({ contractId, categoryId }) => ({
        url: `/api/legal/service-agreements-individual/${contractId}/social-categories/${categoryId}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: "ServiceAgreementsIndividual" as const, id: contractId },
        { type: "ServiceAgreementsIndividual" as const, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useSubmitServiceContractMutation,
  useUpdateServiceContractMutation,
  useGetServiceContractQuery,
  useGetServiceContractListQuery,
  useSubmitServiceContractForSigningMutation,
  useDownloadServiceContractPreviewMutation,
  useDeleteServiceContractMutation,
  useRevokeServiceContractMutation,
  useUploadEnrollmentFileMutation,
  useDeleteEnrollmentFileMutation,
  useCreateContractorSocialCategoryMutation,
  useUpdateContractorSocialCategoryMutation,
  useDeleteContractorSocialCategoryMutation,
} = serviceContractApi;
